// Packs every generated sprite into one texture atlas at boot. Nothing is
// fetched: the pack draws itself, so the whole game ships as code.
import * as pc from "playcanvas";
import { Lib, packSprites, type Grid, type SpriteEntry } from "../vendor/spritelib.ts";
import { extraSprites } from "./extras.ts";

export interface Frame {
  /** uv rect in the atlas, v0 = top edge */
  u0: number;
  v0: number;
  u1: number;
  v1: number;
  /** source size in pixels */
  w: number;
  h: number;
}

const ATLAS_W = 1024;

export class Atlas {
  readonly canvas: HTMLCanvasElement;
  readonly texture: pc.Texture;
  private readonly frames = new Map<string, Frame>();
  private readonly grids = new Map<string, Grid>();
  private readonly blended = new Set<string>();
  private readonly entries: SpriteEntry[];

  constructor(device: pc.GraphicsDevice) {
    // the pack, its three companion files, then whatever the game draws itself
    this.entries = packSprites();

    const base = new Map<string, Grid>();
    const items: { id: string; grid: Grid }[] = [];
    for (const e of this.entries) {
      if (base.has(e.id)) continue; // first registration wins
      base.set(e.id, e.grid);
      items.push({ id: e.id, grid: e.grid });
      if (e.blended) this.blended.add(e.id);
    }
    for (const e of extraSprites(base)) {
      items.push({ id: e.id, grid: e.grid });
      if (e.blended) this.blended.add(e.id);
    }
    for (const it of items) this.grids.set(it.id, it.grid);

    const PAD = 1;
    const width = ATLAS_W;
    // shelf pack, tallest first
    const order = [...items].sort((a, b) => b.grid.h - a.grid.h);
    let x = PAD;
    let y = PAD;
    let shelf = 0;
    const placed: { id: string; grid: Grid; x: number; y: number }[] = [];
    for (const it of order) {
      if (x + it.grid.w + PAD > width) {
        x = PAD;
        y += shelf + PAD;
        shelf = 0;
      }
      placed.push({ id: it.id, grid: it.grid, x, y });
      x += it.grid.w + PAD;
      shelf = Math.max(shelf, it.grid.h);
    }
    const height = nextPow2(y + shelf + PAD);

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d")!;
    ctx.imageSmoothingEnabled = false;

    // Sample texel centres, not texel edges: quads land exactly on pixel
    // boundaries, so an un-inset uv rounds either way along a shared edge and
    // leaks the transparent gutter between sprites as a one-pixel seam.
    for (const p of placed) {
      drawGrid(ctx, p.grid, p.x, p.y);
      this.frames.set(p.id, {
        u0: (p.x + 0.5) / width,
        v0: (p.y + 0.5) / height,
        u1: (p.x + p.grid.w - 0.5) / width,
        v1: (p.y + p.grid.h - 0.5) / height,
        w: p.grid.w,
        h: p.grid.h,
      });
    }

    this.canvas = canvas;
    this.texture = new pc.Texture(device, {
      name: "farm-atlas",
      width,
      height,
      format: pc.PIXELFORMAT_RGBA8,
      mipmaps: false,
      minFilter: pc.FILTER_NEAREST,
      magFilter: pc.FILTER_NEAREST,
      addressU: pc.ADDRESS_CLAMP_TO_EDGE,
      addressV: pc.ADDRESS_CLAMP_TO_EDGE,
    });
    this.texture.setSource(canvas);
  }

  frame(id: string): Frame {
    const f = this.frames.get(id);
    if (!f) throw new Error(`sprite "${id}" is not in the atlas`);
    return f;
  }

  /** Like `frame`, but falls back to `alt` when `id` was never drawn. */
  frameOr(id: string, alt: string): Frame {
    return this.frames.get(id) ?? this.frame(alt);
  }

  has(id: string): boolean {
    return this.frames.has(id);
  }

  /** True for sprites that carry partial alpha and belong on the FX layer. */
  isBlended(id: string): boolean {
    return this.blended.has(id);
  }

  get size(): number {
    return this.frames.size;
  }

  grid(id: string): Grid | undefined {
    return this.grids.get(id);
  }

  /** Standalone canvas for a sprite, used by the DOM hud. */
  icon(id: string, scale = 2): HTMLCanvasElement {
    const g = this.grids.get(id);
    if (!g) throw new Error(`sprite "${id}" is not in the atlas`);
    return Lib.toCanvas(g, scale);
  }

  /** Same, but silently returns an empty canvas for an unknown id. */
  iconOr(id: string, alt: string, scale = 2): HTMLCanvasElement {
    return this.icon(this.grids.has(id) ? id : alt, scale);
  }
}

function drawGrid(ctx: CanvasRenderingContext2D, g: Grid, ox: number, oy: number): void {
  for (let y = 0; y < g.h; y++) {
    for (let x = 0; x < g.w; x++) {
      const key = g.d[y * g.w + x]!;
      // FX sprites are the one place the pack allows partial alpha
      const c = Lib.PAL[key] ?? Lib.PAL_FX[key];
      if (!c) continue;
      ctx.fillStyle = c;
      ctx.fillRect(ox + x, oy + y, 1, 1);
    }
  }
}

function nextPow2(v: number): number {
  let p = 1;
  while (p < v) p *= 2;
  return p;
}
